import { ConnectionError, FsEntryType, } from '@lucidlink/sdk';
import { translate } from '@lucidlink/uicore';
import { BATCH_ENTRY_QUEUE_CAPACITY, DEFAULT_ARCHIVE_BASE_NAME, DOWNLOAD_ENTRY_KINDS, ZERO_BYTES_BIG_INT, } from '../../constants';
import { generateArchiveName } from '../archive-name';
import { createArchiveStream } from '../archive-stream';
import { AsyncQueue } from '../async-queue';
import { bytesToMb, getFolderDepth } from '../telemetry';
import { walkDownloadableFolder } from '../walk-downloadable-files';
import { readDirWithRetry } from './read-dir-with-retry';
const ARCHIVE_ARTIFACT_ID_PREFIX = 'archive';
const feedDownloadableEntries = async ({ entryQueue, readDir, rootFolder, signal, }) => {
    const discovered = walkDownloadableFolder(rootFolder, { readDir, signal });
    for await (const entry of discovered) {
        await entryQueue.push(entry);
    }
};
async function* readArchive(reader) {
    let result = await reader.read();
    while (!result.done) {
        yield result.value;
        result = await reader.read();
    }
}
const walkFolderEntries = async ({ abortController, onRetry, plugConfiguredFilespaceClient, rootFolder, }) => {
    const entryQueue = new AsyncQueue({
        capacity: BATCH_ENTRY_QUEUE_CAPACITY,
    });
    const { promise: clientReady, reject: failClientReady, resolve: markClientReady, } = Promise.withResolvers();
    // Runs alongside the archive stream; a failed walk reaches the consumer through the queue.
    const walkPromise = (async () => {
        try {
            await plugConfiguredFilespaceClient((fsClient) => {
                markClientReady();
                return feedDownloadableEntries({
                    entryQueue,
                    readDir: (entryKey) => readDirWithRetry(fsClient.fileSystem(), entryKey, {
                        onRetry,
                        signal: abortController.signal,
                    }),
                    rootFolder,
                    signal: abortController.signal,
                });
            });
            entryQueue.close();
        }
        catch (error) {
            failClientReady(error);
            entryQueue.fail(error);
        }
    })();
    const stop = async () => {
        // Closing the queue wakes a walk blocked in `push()`; the abort stops it reading further.
        abortController.abort();
        entryQueue.close();
        await walkPromise;
    };
    // An unreachable filespace must fail before a service worker is registered.
    try {
        await clientReady;
    }
    catch (error) {
        await stop();
        throw error;
    }
    return { discoveredEntries: entryQueue, stop };
};
const openFolderSource = async ({ abortController, createChunkDownloader, onRetry, plugConfiguredFilespaceClient, rootFolder, }) => {
    const walk = await walkFolderEntries({
        abortController,
        onRetry,
        plugConfiguredFilespaceClient,
        rootFolder,
    });
    let archivedBytes = ZERO_BYTES_BIG_INT;
    let archivedFileCount = 0;
    let deepestFolderDepth = 0;
    let skippedFilesCount = 0;
    const skipUnreadableExternalFile = (error) => {
        // A bucket without CORS fails like a network drop, so skip the file
        // rather than retry it. A ConnectionError is the hub socket, not the
        // bucket: it gets the usual retries first and then fails the download.
        if (error instanceof ConnectionError ||
            abortController.signal.aborted) {
            return false;
        }
        skippedFilesCount += 1;
        return true;
    };
    const archiveReader = createArchiveStream({
        createChunkDownloader,
        downloadableEntries: walk.discoveredEntries,
        onFileArchived: (file) => {
            archivedFileCount += 1;
            archivedBytes += file.fsEntrySize;
            deepestFolderDepth = Math.max(deepestFolderDepth, getFolderDepth(file.zipPath));
        },
        skipUnreadableExternalFile,
    }).getReader();
    const transferFacts = () => ({
        downloadedFilesCount: archivedFileCount,
        downloadedSize: bytesToMb(archivedBytes),
        folderDepth: deepestFolderDepth,
        kind: DOWNLOAD_ENTRY_KINDS.FOLDER,
        skippedFilesCount,
    });
    return {
        chunks: readArchive(archiveReader),
        cleanUp: async () => {
            try {
                await archiveReader.cancel();
            }
            catch {
                // Cancellation errors are not actionable.
            }
            await walk.stop();
        },
        transferFacts,
    };
};
export const describeFolderDownload = ({ entryId, entryName, fsId, plugConfiguredFilespaceClient, }, { abortController, createChunkDownloader, onRetry, scope, }) => {
    // The root folder is the archive's first entry and client-zip rejects an
    // entry without a name, e.g. while the current folder's path still loads.
    const rootFolder = {
        key: { id: entryId, type: FsEntryType.DIR },
        name: entryName || DEFAULT_ARCHIVE_BASE_NAME,
    };
    return {
        artifact: {
            id: `${ARCHIVE_ARTIFACT_ID_PREFIX}-${scope}`,
            name: generateArchiveName(rootFolder.name),
        },
        batch: { folderId: entryId, fsId },
        copy: {
            completed: translate('Download completed.'),
            failed: translate('The downloaded archive is incomplete. Please try again.'),
        },
        entryFacts: { kind: DOWNLOAD_ENTRY_KINDS.FOLDER },
        openSource: () => openFolderSource({
            abortController,
            createChunkDownloader,
            onRetry,
            plugConfiguredFilespaceClient,
            rootFolder,
        }),
    };
};
